import { Box } from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { ResidenceTypeModel } from "../clients/KompasApi";
import { ResidenceTypeImage } from "./ResidenceTypeImage";

const labels = [
  "G",
  "F",
  "E",
  "D",
  "C",
  "B",
  "A",
  "A+",
  "A++",
  "A+++",
  "A++++",
] as const;

const segmentsScale = 16;
const svgSize = 300;
const radius = svgSize * 0.5;
const width = svgSize * 0.03;
const margin = 0.1;
const rotationOffset = 147.5;

const polarToCartesian = (x: number, y: number, r: number, degrees: number) => {
  const radians = (degrees * Math.PI) / 180.0;
  return [x + r * Math.cos(radians), y + r * Math.sin(radians)];
};

const segmentPath = (
  x: number,
  y: number,
  r0: number,
  r1: number,
  d0: number,
  d1: number
) => {
  const arc = Math.abs(d0 - d1) > 180 ? 1 : 0;
  const point = (r: number, degree: number) =>
    polarToCartesian(x, y, r, degree)
      .map((n) => n.toPrecision(5))
      .join(",");
  return [
    `M${point(r0, d0)}`,
    `A${r0},${r0},0,${arc},1,${point(r0, d1)}`,
    `L${point(r1, d1)}`,
    `A${r1},${r1},0,${arc},0,${point(r1, d0)}`,
    "Z",
  ].join("");
};

function Triangle({ label }: { label: (typeof labels)[number] }) {
  const index = useMemo(() => labels.indexOf(label), [label]);
  const rotation = useMemo(() => {
    if (index === -1) return 0;

    return (
      (360 / segmentsScale) * (index + 1) -
      rotationOffset +
      180 / segmentsScale +
      1.25
    );
  }, [index]);

  if (index === -1) return null;

  return (
    <path
      d={`M ${svgSize * 0.5},5 ${svgSize * 0.5 + 18},${svgSize * 0.08} ${
        svgSize * 0.5 - 18
      },${svgSize * 0.08}z`}
      fill={`var(--chakra-colors-labels-${label.replaceAll("+", "\\+")})`}
      height={svgSize}
      stroke="white"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="4px"
      transform={`rotate(${rotation} ${svgSize * 0.5} ${svgSize * 0.5})`}
      width={svgSize}
    />
  );
}

function Segment({
  index,
  label,
  active,
}: {
  index: number;
  label: (typeof labels)[number];
  active: boolean;
}) {
  const fill = useMemo(
    () =>
      active
        ? `var(--chakra-colors-labels-${label.replaceAll("+", "\\+")})`
        : "var(--chakra-colors-brand-gray-110)",
    [active, label]
  );

  const center = svgSize / 2;
  const degrees = 360 / segmentsScale;
  const start = degrees * index + rotationOffset;
  const end = degrees * (index + 1 - margin) + rotationOffset;
  const path = segmentPath(center, center, radius, radius - width, start, end);

  return <path d={path} fill={fill} />;
}

const isLabel = (test: string): test is (typeof labels)[number] =>
  labels.includes(test as (typeof labels)[number]);

const toLabel = (label: string) => (isLabel(label) ? label : labels.at(-1)!);

interface Props {
  currentLabel: string;
  targetLabel?: string;
  residenceType: ResidenceTypeModel;
}

export function RadialThermometer({
  currentLabel: currentLabelRaw,
  targetLabel: targetLabelRaw,
  residenceType,
}: Props) {
  const currentLabel = toLabel(currentLabelRaw);
  const targetLabel = targetLabelRaw ? toLabel(targetLabelRaw) : currentLabel;

  const startLabelIndex = useMemo(
    () => labels.indexOf(currentLabel),
    [currentLabel]
  );
  const endLabelIndex = useMemo(
    () => labels.indexOf(targetLabel),
    [targetLabel]
  );

  const isActive = useCallback(
    (index: number) => index >= startLabelIndex && index <= endLabelIndex,
    [startLabelIndex, endLabelIndex]
  );

  const segments = useMemo(
    () =>
      labels.map((label, index) => (
        <Segment
          key={label}
          active={isActive(index)}
          index={index}
          label={label}
        />
      )),
    [isActive]
  );

  return (
    <Box
      __css={{
        svg: {
          path: {
            transition: "transform 0.1s ease-in-out, fill 0.1s ease-in-out",
          },
        },
      }}
      h={`${svgSize * 0.75}px`}
      mx="auto"
      overflow="hidden"
      position="relative"
      w={svgSize}
    >
      <ResidenceTypeImage
        fallback={residenceType.name}
        id={residenceType.id}
        round
        size={svgSize}
      />
      <svg height={svgSize} width={svgSize}>
        {segments}
        <Triangle label={currentLabel} />
        {targetLabel !== currentLabel && <Triangle label={targetLabel} />}
      </svg>
    </Box>
  );
}
